import { type ComponentType } from 'react'

import {
  type AppNavLoader,
  type AppNavProps,
  type RegisteredApp,
} from '~/shared/appContract'

/**
 * Every app's primary nav once loaded, keyed on the loader that produced it.
 *
 * The cache is how a nav crosses from the asynchronous load that produces it to the synchronous
 * render that needs it: the app's mount route fills it, the header reads it, and the header therefore
 * has a plain component to render rather than a promise to wait on.
 *
 * Loaded components rather than `React.lazy` ones, and that part is load-bearing. `lazy()` suspends on
 * its first render even when the module is already in the browser's registry, because it is handed a
 * fresh pending promise each time it initializes; the Suspense fallback that then commits holds the
 * reveal for react-dom's `FALLBACK_THROTTLE_MS`, so a nav whose code arrived before the header
 * rendered still appears a flat 300ms after the page it sits above. See Atlas ADR-0021.
 *
 * Keyed on the loader rather than on the app id because the loader is what the entry came from, and it
 * is a stable module constant on a registry entry.
 */
const appNavCache = new Map<AppNavLoader, ComponentType<AppNavProps>>()

/**
 * Load the given app's nav and add it to the cache, so the header can read it without suspending.
 * Resolves to nothing.
 *
 * Called from the app's own mount route, whose module the router awaits before it commits the
 * navigation that renders the header. Both of an app's loaders go through one module, so this costs no
 * request of its own, and the component is cached by the time the header first renders.
 *
 * Never rejects. A nav that fails to load is a chunk that failed to load, so `loadRoutes` fails with
 * it and the mount route reports the error; swallowing it here only keeps an app's pages from being
 * taken down a second time by the header, which has nothing to render either way.
 */
export const addToAppNavCache = async (app: RegisteredApp): Promise<void> => {
  const { loadNav } = app

  if (loadNav === undefined || appNavCache.has(loadNav)) {
    return
  }

  try {
    const { Component } = await loadNav()

    appNavCache.set(loadNav, Component)
  } catch {
    // Left out of the cache, so the header renders no nav. The route's own failure is what surfaces.
  }
}

/**
 * The given app's cached nav component, or `undefined` for an app that declares no nav or whose nav is
 * not in the cache. Synchronous by design: see `appNavCache`.
 */
export const getFromAppNavCache = (
  app: RegisteredApp | undefined,
): ComponentType<AppNavProps> | undefined => {
  const loadNav = app?.loadNav

  return loadNav === undefined ? undefined : appNavCache.get(loadNav)
}
